<script setup>
import { computed } from 'vue';
import { Bar, Doughnut, Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  CategoryScale,
  LinearScale,
} from 'chart.js';
import { useI18n } from 'vue-i18n';
import { parseConvFilterParams } from '../helpers/openConversationFilter';

const props = defineProps({
  resultType: { type: String, required: true },
  resultData: { type: Object, required: true },
  convFilterParams: { type: [String, Array, Object], default: null },
});

const emit = defineEmits(['segmentClick']);

const parsedFilterParams = computed(() =>
  parseConvFilterParams(props.convFilterParams)
);

const isClickable = computed(() => !!parsedFilterParams.value);

const mandatoryPositions = computed(() => {
  if (!parsedFilterParams.value) return new Set();
  return new Set(parsedFilterParams.value.mandatory.map(p => p.position));
});

const optionalPositions = computed(() => {
  if (!parsedFilterParams.value) return new Set();
  return new Set(parsedFilterParams.value.optional.map(p => p.position));
});

const onSegmentClick = (rowValues, optionalPosition = null) => {
  if (!parsedFilterParams.value) return;
  emit('segmentClick', {
    filterParams: parsedFilterParams.value,
    rowValues,
    optionalPosition,
  });
};

const toPositional = obj => {
  const columns = props.resultData.columns || [];
  const result = {};
  if (columns.length) {
    columns.forEach((col, i) => {
      if (col in obj) result[`col${i + 1}`] = obj[col];
    });
  } else {
    Object.values(obj).forEach((v, i) => {
      result[`col${i + 1}`] = v;
    });
  }
  return result;
};

const onPieChartClick = (_event, elements) => {
  if (!elements.length || !parsedFilterParams.value) return;
  const idx = elements[0].index;
  const segment = (props.resultData.segments || [])[idx];
  if (segment) onSegmentClick(toPositional(segment));
};

const onBarChartClick = (_event, elements) => {
  if (!elements.length || !parsedFilterParams.value) return;
  const idx = elements[0].index;
  const segment = (props.resultData.segments || [])[idx];
  if (segment) onSegmentClick(toPositional(segment));
};

const onLineChartClick = (_event, elements) => {
  if (!elements.length || !parsedFilterParams.value) return;
  const idx = elements[0].index;
  const rows = props.resultData.series || [];
  const hasSeriesColumn = rows.length > 0 && 'series' in rows[0];
  if (!hasSeriesColumn) {
    const row = rows[idx];
    if (row) onSegmentClick(toPositional(row));
  } else {
    const datasetIdx = elements[0].datasetIndex;
    const seriesNames = [...new Set(rows.map(r => r.series))];
    const periods = [...new Set(rows.map(r => r.period))].sort();
    const seriesName = seriesNames[datasetIdx];
    const period = periods[idx];
    const row = rows.find(r => r.series === seriesName && r.period === period);
    if (row) onSegmentClick(toPositional(row));
  }
};

const onTableCellClick = (row, colIndex) => {
  if (!parsedFilterParams.value) return;
  const columns = props.resultData.columns || [];
  const positional = {};
  columns.forEach((col, i) => {
    positional[`col${i + 1}`] = row[col];
  });

  // 1-based column position
  const position = colIndex + 1;

  // If the clicked column is optional, pass its position
  const optionalPosition = optionalPositions.value.has(position)
    ? position
    : null;
  onSegmentClick(positional, optionalPosition);
};

const isCellClickable = colIndex => {
  if (!isClickable.value) return false;
  const position = colIndex + 1;
  return (
    mandatoryPositions.value.has(position) ||
    optionalPositions.value.has(position)
  );
};

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  CategoryScale,
  LinearScale
);

const { t } = useI18n();

const CHART_COLORS = [
  'rgb(31, 147, 255)',
  'rgb(246, 109, 155)',
  'rgb(120, 82, 238)',
  'rgb(43, 187, 173)',
  'rgb(255, 163, 68)',
  'rgb(101, 116, 205)',
  'rgb(237, 74, 106)',
  'rgb(56, 193, 114)',
];

const pieChartData = data => {
  const segments = data.segments || [];
  return {
    labels: segments.map(s => s.label),
    datasets: [
      {
        data: segments.map(s => s.count),
        backgroundColor: CHART_COLORS.slice(0, segments.length),
      },
    ],
  };
};

const onHoverPointer = (event, elements) => {
  event.native.target.style.cursor = elements.length ? 'pointer' : 'default';
};

const pieChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: { boxWidth: 12, padding: 8, font: { size: 11 } },
    },
  },
  ...(isClickable.value && {
    onClick: onPieChartClick,
    onHover: onHoverPointer,
  }),
}));

const pieChartHeight = data => {
  const count = (data.segments || []).length;
  return Math.max(300, count * 24);
};

const barChartData = data => {
  const segments = data.segments || [];
  return {
    labels: segments.map(s => String(s.label)),
    datasets: [
      {
        label: data.columns?.[1] || 'Count',
        data: segments.map(s => s.count),
        backgroundColor: CHART_COLORS[0],
        borderRadius: 4,
        maxBarThickness: 40,
      },
    ],
  };
};

const barChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    x: { grid: { drawOnChartArea: false } },
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1 },
      grid: { drawOnChartArea: false },
    },
  },
  ...(isClickable.value && {
    onClick: onBarChartClick,
    onHover: onHoverPointer,
  }),
}));

const detectInterval = periods => {
  if (periods.length < 2) return 'day';
  const dates = periods.map(p => new Date(p));
  if (dates.some(d => Number.isNaN(d.getTime()))) return 'raw';
  const diffMs = dates[1] - dates[0];
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  if (diffDays >= 300) return 'year';
  if (diffDays >= 25) return 'month';
  if (diffDays >= 6) return 'week';
  if (diffDays >= 1) return 'day';
  return 'hour';
};

const formatPeriodLabel = (period, interval) => {
  const d = new Date(period);
  if (Number.isNaN(d.getTime())) return String(period);
  switch (interval) {
    case 'year':
      return d.getUTCFullYear().toString();
    case 'month':
      return d.toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
      });
    case 'week':
    case 'day':
      return d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    case 'hour':
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      });
    default:
      return String(period);
  }
};

const toRgba = (rgb, alpha) =>
  rgb.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`);

const lineChartData = data => {
  const rows = data.series || [];
  const hasSeriesColumn = rows.length > 0 && 'series' in rows[0];

  if (!hasSeriesColumn) {
    const periods = rows.map(s => s.period);
    const interval = detectInterval(periods);
    return {
      labels: periods.map(p => formatPeriodLabel(p, interval)),
      datasets: [
        {
          label: data.columns?.[1] || 'Count',
          data: rows.map(s => s.count),
          borderColor: CHART_COLORS[0],
          backgroundColor: toRgba(CHART_COLORS[0], 0.1),
          fill: true,
          tension: 0.3,
          pointRadius: 3,
        },
      ],
    };
  }

  // Multi-series: pivot rows grouped by "series" column
  const periodSet = new Set();
  const seriesMap = new Map();
  rows.forEach(row => {
    periodSet.add(row.period);
    if (!seriesMap.has(row.series)) seriesMap.set(row.series, new Map());
    seriesMap.get(row.series).set(row.period, row.count);
  });

  const periods = [...periodSet].sort();
  const interval = detectInterval(periods);
  const labels = periods.map(p => formatPeriodLabel(p, interval));
  const multiSeries = seriesMap.size > 1;

  const datasets = [...seriesMap.entries()].map(([name, values], i) => {
    const color = CHART_COLORS[i % CHART_COLORS.length];
    return {
      label: name,
      data: periods.map(p => values.get(p) || 0),
      borderColor: color,
      backgroundColor: toRgba(color, multiSeries ? 0.05 : 0.1),
      fill: !multiSeries,
      tension: 0.3,
      pointRadius: 3,
    };
  });

  return { labels, datasets };
};

const lineChartOptions = computed(() => {
  const rows = props.resultData.series || [];
  const hasMultipleSeries =
    rows.length > 0 &&
    'series' in rows[0] &&
    new Set(rows.map(r => r.series)).size > 1;
  const base = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: hasMultipleSeries,
        labels: { boxWidth: 12, padding: 8, font: { size: 11 } },
      },
    },
    scales: {
      x: {
        grid: { drawOnChartArea: false },
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
        grid: { drawOnChartArea: false },
      },
    },
  };
  if (!isClickable.value) return base;
  return {
    ...base,
    onClick: onLineChartClick,
    onHover: onHoverPointer,
  };
});

const getRows = data => {
  return data.rows || data.series || data.segments;
};

const hasTableData = data => {
  const rows = getRows(data);
  return rows !== undefined;
};

const formatResultData = data => {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
};
</script>

<template>
  <div v-if="resultData && Object.keys(resultData).length">
    <!-- Number -->
    <div
      v-if="resultType === 'number'"
      class="text-3xl font-bold text-n-slate-12"
      :class="{ 'cursor-pointer hover:text-n-brand': isClickable }"
      @click="isClickable ? onSegmentClick(toPositional(resultData)) : null"
    >
      {{ resultData.value }}
    </div>

    <!-- Pie Chart -->
    <div
      v-else-if="resultType === 'pie_chart'"
      :style="{ height: pieChartHeight(resultData) + 'px' }"
    >
      <Doughnut :data="pieChartData(resultData)" :options="pieChartOptions" />
    </div>

    <!-- Bar Chart -->
    <div v-else-if="resultType === 'bar_chart'" class="h-[300px]">
      <Bar :data="barChartData(resultData)" :options="barChartOptions" />
    </div>

    <!-- Time Series (Line chart) -->
    <div v-else-if="resultType === 'time_series'" class="h-[300px]">
      <Line :data="lineChartData(resultData)" :options="lineChartOptions" />
    </div>

    <!-- Table -->
    <div v-else-if="hasTableData(resultData)" class="overflow-x-auto">
      <table class="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th
              v-for="col in resultData.columns"
              :key="col"
              class="border-b border-n-weak bg-n-solid-2 px-3 py-1.5 text-left font-semibold text-n-slate-11"
            >
              {{ col }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIdx) in getRows(resultData)"
            :key="rowIdx"
            class="border-b border-n-weak"
          >
            <td
              v-for="(col, colIdx) in resultData.columns"
              :key="col"
              class="px-3 py-1.5 text-n-slate-12"
              :class="{
                'cursor-pointer hover:bg-n-alpha-2 hover:text-n-brand':
                  isCellClickable(colIdx),
              }"
              @click="
                isCellClickable(colIdx) ? onTableCellClick(row, colIdx) : null
              "
            >
              {{ row[col] }}
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="!getRows(resultData).length" class="py-2 text-n-slate-10">
        {{ t('DATA_ASSISTANT.NO_ROWS') }}
      </p>
    </div>

    <!-- Fallback: raw JSON -->
    <pre
      v-else
      class="overflow-x-auto rounded bg-n-solid-1 p-2 text-xs text-n-slate-11"
      >{{ formatResultData(resultData) }}
    </pre>
  </div>
</template>
